/**
 * Service de gestion des lots et DLC
 * Traçabilité alimentaire pour StockChef
 */

import { supabase } from '@/lib/supabase';
import type {
  Lot,
  LotFilters,
  LotStatut,
  CreateLotInput,
  UpdateLotInput,
  ConsumeLotInput,
  TransferLotInput,
  MouvementLot,
  Fournisseur,
  CreateFournisseurInput,
  UpdateFournisseurInput,
  FournisseurFilters,
  LotStats,
  ExpiringLotSummary,
  DLCUrgence,
  ZoneStockage,
} from '@/types/inventory.types';

class LotService {
  // ============================================
  // LOTS - CRUD
  // ============================================

  /**
   * Récupérer tous les lots d'un restaurant
   */
  async getLots(restaurantId: string, filters?: LotFilters): Promise<Lot[]> {
    let query = supabase
      .from('lots')
      .select(`
        *,
        produit:produits(id, nom, categorie, unite, code_barre),
        fournisseur:fournisseurs(id, nom)
      `)
      .eq('restaurant_id', restaurantId)
      .order('dlc', { ascending: true, nullsFirst: false });

    // Appliquer les filtres
    if (filters?.produit_id) {
      query = query.eq('produit_id', filters.produit_id);
    }
    if (filters?.zone) {
      query = query.eq('zone', filters.zone);
    }
    if (filters?.statut) {
      query = query.eq('statut', filters.statut);
    }
    if (filters?.fournisseur_id) {
      query = query.eq('fournisseur_id', filters.fournisseur_id);
    }
    if (filters?.search) {
      query = query.ilike('numero_lot', `%${filters.search}%`);
    }
    if (filters?.expiringWithinDays !== undefined) {
      const futureDate = new Date();
      futureDate.setDate(futureDate.getDate() + filters.expiringWithinDays);
      query = query
        .not('dlc', 'is', null)
        .lte('dlc', futureDate.toISOString().split('T')[0]);
    }

    const { data, error } = await query;

    if (error) {
      console.warn('Lots not available:', error.message);
      return [];
    }

    return (data || []).map(this.mapLotFromDB);
  }

  /**
   * Récupérer un lot par son ID
   */
  async getLotById(lotId: string): Promise<Lot | null> {
    const { data, error } = await supabase
      .from('lots')
      .select(`
        *,
        produit:produits(id, nom, categorie, unite, code_barre),
        fournisseur:fournisseurs(id, nom)
      `)
      .eq('id', lotId)
      .single();

    if (error) {
      console.error('Error fetching lot:', error);
      return null;
    }

    return this.mapLotFromDB(data);
  }

  /**
   * Créer un nouveau lot
   */
  async createLot(restaurantId: string, input: CreateLotInput, userId?: string): Promise<Lot> {
    const { data, error } = await supabase
      .from('lots')
      .insert({
        restaurant_id: restaurantId,
        produit_id: input.produit_id,
        numero_lot: input.numero_lot,
        date_reception: input.date_reception || new Date().toISOString().split('T')[0],
        dlc: input.dlc || null,
        ddm: input.ddm || null,
        quantite_initiale: input.quantite_initiale,
        quantite_restante: input.quantite_initiale,
        fournisseur_id: input.fournisseur_id || null,
        prix_achat: input.prix_achat || null,
        zone: input.zone,
        statut: 'actif',
        notes: input.notes || null,
        created_by: userId || null,
      })
      .select(`
        *,
        produit:produits(id, nom, categorie, unite),
        fournisseur:fournisseurs(id, nom)
      `)
      .single();

    if (error) {
      console.error('Error creating lot:', error);
      throw error;
    }

    // Enregistrer le mouvement de réception
    await this.createMouvement(data.id, restaurantId, 'reception', input.quantite_initiale, {
      zone_destination: input.zone,
      utilisateur_id: userId,
    });

    return this.mapLotFromDB(data);
  }

  /**
   * Mettre à jour un lot
   */
  async updateLot(lotId: string, input: UpdateLotInput): Promise<Lot> {
    const { data, error } = await supabase
      .from('lots')
      .update({
        ...input,
        updated_at: new Date().toISOString(),
      })
      .eq('id', lotId)
      .select(`
        *,
        produit:produits(id, nom, categorie, unite),
        fournisseur:fournisseurs(id, nom)
      `)
      .single();

    if (error) {
      console.error('Error updating lot:', error);
      throw error;
    }

    return this.mapLotFromDB(data);
  }

  /**
   * Supprimer un lot (soft delete - marquer comme retiré)
   */
  async deleteLot(lotId: string, userId?: string, motif?: string): Promise<void> {
    const lot = await this.getLotById(lotId);
    if (!lot) throw new Error('Lot non trouvé');

    await supabase
      .from('lots')
      .update({
        statut: 'retire',
        updated_at: new Date().toISOString(),
      })
      .eq('id', lotId);

    await this.createMouvement(lotId, lot.restaurant_id, 'retrait', lot.quantite_restante, {
      zone_origine: lot.zone,
      motif,
      utilisateur_id: userId,
    });
  }

  // ============================================
  // LOTS - ACTIONS
  // ============================================

  /**
   * Consommer une quantité d'un lot
   */
  async consumeLot(input: ConsumeLotInput, userId?: string): Promise<Lot> {
    const lot = await this.getLotById(input.lot_id);
    if (!lot) throw new Error('Lot non trouvé');
    if (lot.statut !== 'actif') throw new Error(`Lot non actif: ${lot.statut}`);
    if (input.quantite > lot.quantite_restante) {
      throw new Error(`Quantité insuffisante. Disponible: ${lot.quantite_restante}`);
    }

    const nouvelleQuantite = lot.quantite_restante - input.quantite;
    const nouveauStatut: LotStatut = nouvelleQuantite === 0 ? 'epuise' : 'actif';

    const { data, error } = await supabase
      .from('lots')
      .update({
        quantite_restante: nouvelleQuantite,
        statut: nouveauStatut,
        updated_at: new Date().toISOString(),
      })
      .eq('id', input.lot_id)
      .select(`
        *,
        produit:produits(id, nom, categorie, unite),
        fournisseur:fournisseurs(id, nom)
      `)
      .single();

    if (error) {
      console.error('Error consuming lot:', error);
      throw error;
    }

    await this.createMouvement(input.lot_id, lot.restaurant_id, 'consommation', input.quantite, {
      zone_origine: lot.zone,
      motif: input.motif,
      utilisateur_id: userId,
    });

    return this.mapLotFromDB(data);
  }

  /**
   * Transférer un lot vers une autre zone
   */
  async transferLot(input: TransferLotInput, userId?: string): Promise<Lot> {
    const lot = await this.getLotById(input.lot_id);
    if (!lot) throw new Error('Lot non trouvé');
    if (lot.zone === input.zone_destination) {
      throw new Error('Le lot est déjà dans cette zone');
    }

    const { data, error } = await supabase
      .from('lots')
      .update({
        zone: input.zone_destination,
        updated_at: new Date().toISOString(),
      })
      .eq('id', input.lot_id)
      .select(`
        *,
        produit:produits(id, nom, categorie, unite),
        fournisseur:fournisseurs(id, nom)
      `)
      .single();

    if (error) {
      console.error('Error transferring lot:', error);
      throw error;
    }

    await this.createMouvement(input.lot_id, lot.restaurant_id, 'transfert', lot.quantite_restante, {
      zone_origine: lot.zone,
      zone_destination: input.zone_destination,
      motif: input.motif,
      utilisateur_id: userId,
    });

    return this.mapLotFromDB(data);
  }

  /**
   * Marquer un lot comme périmé
   */
  async markAsExpired(lotId: string, userId?: string): Promise<Lot> {
    const lot = await this.getLotById(lotId);
    if (!lot) throw new Error('Lot non trouvé');

    const { data, error } = await supabase
      .from('lots')
      .update({
        statut: 'perime',
        updated_at: new Date().toISOString(),
      })
      .eq('id', lotId)
      .select(`
        *,
        produit:produits(id, nom, categorie, unite),
        fournisseur:fournisseurs(id, nom)
      `)
      .single();

    if (error) {
      console.error('Error marking lot as expired:', error);
      throw error;
    }

    await this.createMouvement(lotId, lot.restaurant_id, 'peremption', lot.quantite_restante, {
      zone_origine: lot.zone,
      utilisateur_id: userId,
    });

    return this.mapLotFromDB(data);
  }

  // ============================================
  // LOTS - REQUÊTES DLC
  // ============================================

  /**
   * Récupérer les lots expirant bientôt
   */
  async getExpiringLots(restaurantId: string, daysThreshold: number = 7): Promise<ExpiringLotSummary[]> {
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + daysThreshold);

    const { data, error } = await supabase
      .from('lots')
      .select(`
        *,
        produit:produits(id, nom, categorie, unite),
        fournisseur:fournisseurs(id, nom)
      `)
      .eq('restaurant_id', restaurantId)
      .eq('statut', 'actif')
      .gt('quantite_restante', 0)
      .not('dlc', 'is', null)
      .lte('dlc', futureDate.toISOString().split('T')[0])
      .order('dlc', { ascending: true });

    if (error) {
      console.warn('Expiring lots not available:', error.message);
      return [];
    }

    return (data || []).map((item: any) => {
      const lot = this.mapLotFromDB(item);
      const joursRestants = this.calculateDaysRemaining(lot.dlc);

      return {
        lot,
        produit_nom: item.produit?.nom || 'Produit inconnu',
        produit_categorie: item.produit?.categorie || '',
        fournisseur_nom: item.fournisseur?.nom || null,
        jours_restants: joursRestants,
        urgence: this.calculateUrgence(joursRestants),
      };
    });
  }

  /**
   * Récupérer les statistiques des lots
   */
  async getLotStats(restaurantId: string): Promise<LotStats> {
    const { data, error } = await supabase
      .from('lots')
      .select('statut, dlc, quantite_restante')
      .eq('restaurant_id', restaurantId);

    if (error) {
      console.warn('Lot stats not available:', error.message);
      return {
        total: 0,
        actifs: 0,
        epuises: 0,
        perimes: 0,
        expirantBientot: 0,
        critiquesDLC: 0,
      };
    }

    const today = new Date();
    const in3Days = new Date(today);
    in3Days.setDate(in3Days.getDate() + 3);
    const in7Days = new Date(today);
    in7Days.setDate(in7Days.getDate() + 7);

    const lots = data || [];

    return {
      total: lots.length,
      actifs: lots.filter(l => l.statut === 'actif').length,
      epuises: lots.filter(l => l.statut === 'epuise').length,
      perimes: lots.filter(l => l.statut === 'perime').length,
      expirantBientot: lots.filter(l => {
        if (l.statut !== 'actif' || !l.dlc || l.quantite_restante <= 0) return false;
        const dlc = new Date(l.dlc);
        return dlc <= in7Days && dlc > today;
      }).length,
      critiquesDLC: lots.filter(l => {
        if (l.statut !== 'actif' || !l.dlc || l.quantite_restante <= 0) return false;
        const dlc = new Date(l.dlc);
        return dlc <= in3Days;
      }).length,
    };
  }

  /**
   * Suggérer le lot à consommer en priorité (FIFO par DLC)
   */
  async getSuggestedLot(
    restaurantId: string,
    produitId: string,
    zone?: ZoneStockage
  ): Promise<Lot | null> {
    let query = supabase
      .from('lots')
      .select(`
        *,
        produit:produits(id, nom, categorie, unite),
        fournisseur:fournisseurs(id, nom)
      `)
      .eq('restaurant_id', restaurantId)
      .eq('produit_id', produitId)
      .eq('statut', 'actif')
      .gt('quantite_restante', 0)
      .order('dlc', { ascending: true, nullsFirst: false })
      .order('date_reception', { ascending: true })
      .limit(1);

    if (zone) {
      query = query.eq('zone', zone);
    }

    const { data, error } = await query;

    if (error || !data || data.length === 0) {
      return null;
    }

    return this.mapLotFromDB(data[0]);
  }

  // ============================================
  // MOUVEMENTS DE LOTS
  // ============================================

  /**
   * Récupérer l'historique des mouvements d'un lot
   */
  async getLotMovements(lotId: string): Promise<MouvementLot[]> {
    const { data, error } = await supabase
      .from('mouvements_lots')
      .select(`
        *,
        utilisateur:users(id, email, display_name)
      `)
      .eq('lot_id', lotId)
      .order('created_at', { ascending: false });

    if (error) {
      console.warn('Lot movements not available:', error.message);
      return [];
    }

    return (data || []).map((m: any) => ({
      id: m.id,
      lot_id: m.lot_id,
      restaurant_id: m.restaurant_id,
      type: m.type,
      quantite: m.quantite,
      zone_origine: m.zone_origine,
      zone_destination: m.zone_destination,
      motif: m.motif,
      utilisateur_id: m.utilisateur_id,
      created_at: m.created_at,
      utilisateur: m.utilisateur,
    }));
  }

  /**
   * Créer un mouvement de lot (privé)
   */
  private async createMouvement(
    lotId: string,
    restaurantId: string,
    type: MouvementLot['type'],
    quantite: number,
    options?: {
      zone_origine?: ZoneStockage | null;
      zone_destination?: ZoneStockage | null;
      motif?: string | null;
      utilisateur_id?: string | null;
    }
  ): Promise<void> {
    const { error } = await supabase.from('mouvements_lots').insert({
      lot_id: lotId,
      restaurant_id: restaurantId,
      type,
      quantite,
      zone_origine: options?.zone_origine || null,
      zone_destination: options?.zone_destination || null,
      motif: options?.motif || null,
      utilisateur_id: options?.utilisateur_id || null,
    });

    if (error) {
      console.error('Error creating lot movement:', error);
      // Non-bloquant, on continue même si l'historique échoue
    }
  }

  // ============================================
  // FOURNISSEURS
  // ============================================

  /**
   * Récupérer tous les fournisseurs
   */
  async getFournisseurs(restaurantId: string, filters?: FournisseurFilters): Promise<Fournisseur[]> {
    let query = supabase
      .from('fournisseurs')
      .select('*')
      .eq('restaurant_id', restaurantId)
      .order('nom', { ascending: true });

    if (filters?.activeOnly !== false) {
      query = query.eq('is_active', true);
    }
    if (filters?.search) {
      query = query.ilike('nom', `%${filters.search}%`);
    }

    const { data, error } = await query;

    if (error) {
      console.warn('Fournisseurs not available:', error.message);
      return [];
    }

    return data || [];
  }

  /**
   * Créer un fournisseur
   */
  async createFournisseur(restaurantId: string, input: CreateFournisseurInput): Promise<Fournisseur> {
    const { data, error } = await supabase
      .from('fournisseurs')
      .insert({
        restaurant_id: restaurantId,
        nom: input.nom,
        contact: input.contact || null,
        email: input.email || null,
        telephone: input.telephone || null,
        adresse: input.adresse || null,
        reference_interne: input.reference_interne || null,
        notes: input.notes || null,
        is_active: true,
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating fournisseur:', error);
      throw error;
    }

    return data;
  }

  /**
   * Mettre à jour un fournisseur
   */
  async updateFournisseur(fournisseurId: string, input: UpdateFournisseurInput): Promise<Fournisseur> {
    const { data, error } = await supabase
      .from('fournisseurs')
      .update({
        ...input,
        updated_at: new Date().toISOString(),
      })
      .eq('id', fournisseurId)
      .select()
      .single();

    if (error) {
      console.error('Error updating fournisseur:', error);
      throw error;
    }

    return data;
  }

  // ============================================
  // HELPERS
  // ============================================

  /**
   * Mapper les données DB vers le type Lot
   */
  private mapLotFromDB(data: any): Lot {
    const joursRestants = this.calculateDaysRemaining(data.dlc);

    return {
      id: data.id,
      restaurant_id: data.restaurant_id,
      produit_id: data.produit_id,
      numero_lot: data.numero_lot,
      date_reception: data.date_reception,
      dlc: data.dlc,
      ddm: data.ddm,
      quantite_initiale: data.quantite_initiale,
      quantite_restante: data.quantite_restante,
      fournisseur_id: data.fournisseur_id,
      prix_achat: data.prix_achat,
      zone: data.zone,
      statut: data.statut,
      notes: data.notes,
      created_by: data.created_by,
      created_at: data.created_at,
      updated_at: data.updated_at,
      produit: data.produit,
      fournisseur: data.fournisseur,
      jours_restants: joursRestants,
      urgence: this.calculateUrgence(joursRestants),
    };
  }

  /**
   * Calculer les jours restants avant DLC
   */
  private calculateDaysRemaining(dlc: string | null): number {
    if (!dlc) return Infinity;
    const dlcDate = new Date(dlc);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffTime = dlcDate.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  /**
   * Calculer le niveau d'urgence DLC
   */
  private calculateUrgence(joursRestants: number): DLCUrgence {
    if (joursRestants <= 0) return 'expire';
    if (joursRestants <= 3) return 'critique';
    if (joursRestants <= 7) return 'attention';
    return 'ok';
  }
}

export const lotService = new LotService();
